export interface PackageFeature {
  title: string;
  description: string;
  icon: string;
}

export interface PricingTier {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  ctaUrl: string;
  badge?: string;
}

export interface PackageSpec {
  id: string;
  title: string;
  subtitle: string;
  whatItIncludes: string[];
  disclaimer: string;
  howItHelps: string;
  howItHelpsDetail: string;
}

export const includedFeatures: PackageFeature[] = [
  {
    title: "The Safe Dental Tourism System",
    description:
      "Common patient mistakes, clinic and dentist red flags, treatment planning, insurance parameters, recovery metrics, and maintenance scheduling.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Enhanced Vetting Checklist",
    description:
      "Strategic questioning criteria, clinical license verification pipelines, cross-provider performance tracking matrices, and threat indicator identification routines.",
    icon: "ri-clipboard-line",
  },
  {
    title: "Specialist Verification Guide",
    description:
      "Analysis of foreign credentials, deceptive marketing filtering models, and regional criteria profiles across Spain, Turkey, Poland, and Hungary.",
    icon: "ri-user-search-line",
  },
  {
    title: "True Treatment Cost Calculator",
    description:
      "Comprehensive fiscal accounting engine for baseline treatments, travel logs, accommodation profiles, living expenditures, and unlisted accessory liabilities.",
    icon: "ri-calculator-line",
  },
];

export const pricingTiers: PricingTier[] = [
  {
    id: "checklist",
    name: "Enhanced Vetting Checklist",
    price: "£19",
    period: "one-time",
    description:
      "A structured professional evaluation directory holding 45+ critical checks to securely audit overseas practices before capital allocation.",
    features: [
      "Enhanced Vetting Checklist asset",
      "Isolate operational structural faults early",
      "Execute advanced interview queries",
      "Compare clinical alternatives securely",
    ],
    cta: "Get Checklist →",
    ctaUrl: "https://dentalsafetyabroad.gumroad.com/l/fqwuvp",
  },
  {
    id: "essentials",
    name: "Essentials Package",
    price: "£49",
    period: "one-time",
    description:
      "A functional, streamlined system configuration for clients prioritizing fast, responsive foundational guidance over deeper legal mechanics.",
    features: [
      "Safe Dental Tourism Essentials",
      "True Treatment Cost Calculator",
      "Practical consumer-centric workflows",
      "Safer comparison structures",
    ],
    cta: "Get Essentials Package →",
    ctaUrl: "https://dentalsafetyabroad.gumroad.com/l/kyjdql",
    badge: "MOST POPULAR",
  },
  {
    id: "complete",
    name: "Complete Protection System",
    price: "£99",
    period: "one-time",
    description:
      "Our total risk mitigation architecture built specifically for patients considering extensive surgical procedures abroad.",
    features: [
      "Complete Safe Dental Tourism System",
      "Enhanced Vetting Checklist integration",
      "Specialist Verification Handbook",
      "True Cost Calculator toolkit access",
      "Comprehensive longitudinal planning data",
    ],
    cta: "Get Complete Protection System →",
    ctaUrl: "https://dentalsafetyabroad.gumroad.com/l/fsnhnf",
  },
];

export const packageSpecs: PackageSpec[] = [
  {
    id: "checklist",
    title: "Enhanced Vetting Checklist",
    subtitle:
      "A structured professional checklist, with 45+ key questions, which is designed to help you assess both the clinic and the individual dentist who will perform your treatment.",
    whatItIncludes: [
      "Clinic registration verification steps",
      "Dentist qualification checks",
      "Professional indemnity verification workflows",
      "Sterilisation and infrastructure testing parameters",
      "Warranty framework and guarantee coverage audits",
      "Longitudinal aftercare validation tracking",
      "Objective comparison framework assets",
    ],
    disclaimer:
      "Many patients spend weeks comparing prices but never properly assess the clinic or dentist providing the treatment. One of the biggest risks in dental tourism is assuming that a well-designed website or impressive Instagram account reflects clinical quality. It doesn't.",
    howItHelps:
      "Most patients have never been shown how to properly assess a dental clinic. This checklist provides a practical framework that helps you ask the right questions, identify warning signs and compare clinics objectively rather than relying on marketing claims, online reviews or social media content.",
    howItHelpsDetail:
      "By identifying potential concerns before you pay a deposit, you can significantly reduce the risk of choosing an unsuitable provider.",
  },
  {
    id: "essentials",
    title: "Essentials Package",
    subtitle:
      "A practical and simplified version of the Safe Dental Tourism System designed for patients who want clear guidance without information overload.",
    whatItIncludes: [
      "Safe Dental Tourism Essentials Guide documentation",
      "True Dental Treatment Cost Calculator software template",
      "Research and structured comparison framework",
      "Treatment operational sequencing logs",
      "Common operational red flags and warning signs catalogs",
    ],
    disclaimer:
      "Many patients start their research by comparing prices. Unfortunately, the cheapest treatment can often become the most expensive if complications occur later.",
    howItHelps:
      "This package helps you understand the key factors that influence treatment quality, safety and long-term outcomes. It also includes the True Dental Treatment Cost Calculator, which helps you compare not only treatment fees but also flights, accommodation, additional visits, recovery time and other hidden costs that are often overlooked.",
    howItHelpsDetail:
      "The result is a much more realistic comparison of treatment options and a more informed purchasing decision.",
  },
  {
    id: "complete",
    title: "Complete Protection System",
    subtitle:
      "Our most comprehensive patient protection package covering every stage of the dental tourism journey, from initial research through to long-term maintenance.",
    whatItIncludes: [
      "The Complete Safe Dental Tourism System Manual",
      "Enhanced Vetting Checklist deployment module",
      "Dental Specialist Verification Guide reference keys",
      "True Dental Treatment Cost Calculator processing engine",
      "Treatment strategy and clinical recovery roadmap parameters",
      "Indemnity insurance and documentation validation systems",
      "Long-term structural maintenance logs",
    ],
    disclaimer:
      "The majority of serious problems arise before treatment even begins.",
    howItHelps:
      "Many patients assume titles such as Implantologist, Smile Designer, Cosmetic Dentist or Hollywood Smile Specialist represent recognised specialist qualifications. In many countries they do not. These are often marketing terms rather than regulated professional titles.",
    howItHelpsDetail:
      "Understanding which specialist is appropriately qualified for a procedure can significantly reduce risk and improve long-term treatment outcomes. For example, complex implant surgery, bone grafting, gum surgery, orthodontics and full-mouth rehabilitation often require different specialist skills and training.",
  },
];

export const dueDiligenceBenefits = [
  "Avoid unsafe clinics and unqualified dentists",
  "Compare providers properly and confidently",
  "Identify hidden costs before booking",
  "Understand genuine specialist qualifications",
  "Protect your long-term oral health investment",
  "Make evidence-based treatment decisions",
];